/**
 * React hook for fetching comparisons and responses from the API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchComparisons,
  fetchResponses,
  ENABLE_COMPARISON_RESPONSE_CACHE,
  getComparisonResponsesFromMemory,
  hydrateComparisonResponses,
} from '../lib/api/results';
import { logger } from '../lib/logging';
import type {
  Comparison,
  Response,
  ComparisonFilters,
} from '../lib/types/results';

interface UseComparisonsResult {
  comparisons: Comparison[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

interface UseResponsesResult {
  responses: Response[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdatedAt: number | null;
  refetch: () => void;
}

/**
 * Hook to fetch comparisons with pagination and sorting
 */
export function useComparisons(
  filters: ComparisonFilters = {}
): UseComparisonsResult {
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(filters.offset ?? 0);
  const latestRequestRef = useRef(0);

  const limit = filters.limit ?? 20;
  const sort = filters.sort ?? '-createdAt';
  const comparisonId = filters.comparisonId;

  const loadPage = useCallback(async (
    offset: number,
    mode: 'replace' | 'append'
  ) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    if (mode === 'append') {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    console.log('[useComparisons] Fetching comparisons:', { comparisonId, limit, offset, sort });

    try {
      const data = await fetchComparisons({ comparisonId, limit, offset, sort });

      console.log('[useComparisons] Received data:', {
        comparisonsCount: data.comparisons.length,
        comparisons: data.comparisons,
        hasMore: data.pagination.hasMore
      });

      if (latestRequestRef.current !== requestId) {
        console.log('[useComparisons] Request outdated, ignoring response');
        return;
      }

      setHasMore(data.pagination.hasMore);
      setComparisons(prev => {
        const newComparisons = mode === 'append' && prev.length > 0
          ? (() => {
              const merged = new Map(prev.map(item => [item.id, item]));
              data.comparisons.forEach(item => {
                merged.set(item.id, item);
              });
              return Array.from(merged.values());
            })()
          : data.comparisons;

        console.log('[useComparisons] Setting comparisons state:', {
          mode,
          previousCount: prev.length,
          newCount: newComparisons.length,
          comparisons: newComparisons
        });

        return newComparisons;
      });

      logger.info('comparisons_page_loaded', {
        returned: data.comparisons.length,
        limit,
        offset,
        hasMore: data.pagination.hasMore,
      }, {
        op: 'comparisons_page_loaded',
        component: 'useComparisons',
        success: true,
      });
    } catch (err) {
      if (latestRequestRef.current !== requestId) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to fetch comparisons';
      setError(message);

      logger.error('comparisons_page_load_failed', {
        limit,
        offset,
        err_message: message,
      }, {
        op: 'comparisons_page_load_failed',
        component: 'useComparisons',
        success: false,
      });
    } finally {
      if (latestRequestRef.current !== requestId) {
        return;
      }

      if (mode === 'append') {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [comparisonId, limit, sort]);

  const resetAndLoad = useCallback(() => {
    offsetRef.current = filters.offset ?? 0;
    setComparisons([]);
    return loadPage(offsetRef.current, 'replace');
  }, [filters.offset, loadPage]);

  useEffect(() => {
    void resetAndLoad();
  }, [resetAndLoad, limit, sort]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) {
      return;
    }
    const nextOffset = offsetRef.current + limit;
    offsetRef.current = nextOffset;
    void loadPage(nextOffset, 'append');
  }, [hasMore, limit, loadPage, loadingMore]);

  const refetch = useCallback(() => {
    offsetRef.current = filters.offset ?? 0;
    void loadPage(offsetRef.current, 'replace');
  }, [filters.offset, loadPage]);

  return {
    comparisons,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
  };
}

/**
 * Hook to fetch responses for ALL comparisons (for Responses view)
 * S1: Load all user Responses from DB on #view=responses
 */
export function useAllResponses(comparisonIds: string[]): UseResponsesResult {
  const [responses, setResponses] = useState<Response[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const latestRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (comparisonIds.length === 0) {
      latestRequestRef.current += 1;
      setResponses([]);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      setLastUpdatedAt(null);
      logger.debug('useAllResponses_skipped_no_comparisons', undefined, {
        component: 'useAllResponses',
        op: 'useAllResponses_fetch'
      });
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    setLoading(true);
    setError(null);
    const correlationId = `useAllResponses_${Date.now()}_${requestId}`;

    logger.info('useAllResponses_fetch_start', {
      comparisonCount: comparisonIds.length,
    }, {
      correlationId,
      component: 'useAllResponses',
      op: 'useAllResponses_fetch'
    });

    try {
      // Fetch responses for all comparisons in parallel
      const results = await Promise.allSettled(
        comparisonIds.map(id => fetchResponses(id, { correlationId }))
      );

      if (latestRequestRef.current !== requestId) {
        logger.debug('useAllResponses_fetch_stale_result', {}, {
          correlationId,
          component: 'useAllResponses',
          op: 'useAllResponses_fetch'
        });
        return;
      }

      // Combine all responses, ignoring failed fetches
      const allResponses: Response[] = [];
      let errorCount = 0;

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          allResponses.push(...result.value.responses);
        } else {
          errorCount++;
          logger.warn('useAllResponses_partial_failure', {
            comparisonId: comparisonIds[index],
            error: result.reason instanceof Error ? result.reason.message : 'Unknown error'
          }, {
            correlationId,
            component: 'useAllResponses',
            op: 'useAllResponses_fetch'
          });
        }
      });

      setResponses(allResponses);
      setLastUpdatedAt(Date.now());
      setLoading(false);
      setRefreshing(false);

      // Set error only if ALL fetches failed
      if (errorCount === comparisonIds.length) {
        setError('Failed to fetch any responses');
      } else if (errorCount > 0) {
        setError(`Failed to fetch ${errorCount} of ${comparisonIds.length} comparisons`);
      }

      logger.info('useAllResponses_fetch_success', {
        comparisonCount: comparisonIds.length,
        responseCount: allResponses.length,
        errorCount,
      }, {
        correlationId,
        component: 'useAllResponses',
        op: 'useAllResponses_fetch',
        success: errorCount < comparisonIds.length
      });
    } catch (err) {
      if (latestRequestRef.current !== requestId) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to fetch responses';
      setError(message);
      setResponses([]);
      setLoading(false);
      setRefreshing(false);

      logger.error('useAllResponses_fetch_error', {}, {
        correlationId,
        component: 'useAllResponses',
        op: 'useAllResponses_fetch',
        success: false,
        err_message: message
      });
    }
  }, [comparisonIds.join(',')]); // Depend on serialized IDs

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    responses,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    refetch: fetchData,
  };
}

/**
 * Hook to fetch responses for a specific comparison
 */
export function useResponses(comparisonId: string | null): UseResponsesResult {
  const [responses, setResponses] = useState<Response[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const latestRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!comparisonId) {
      latestRequestRef.current += 1;
      setResponses([]);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      setLastUpdatedAt(null);
      logger.debug('useResponses_skipped_no_comparison', undefined, {
        component: 'useResponses',
        op: 'useResponses_fetch'
      });
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    setError(null);
    const cacheEnabled = ENABLE_COMPARISON_RESPONSE_CACHE;
    const correlationId = `useResponses_${comparisonId}_${requestId}`;

    const logRendered = (source: 'memory' | 'indexeddb' | 'network' | 'network_refresh' | 'not_modified', count: number) => {
      logger.info('responses_rendered', {
        comparisonId,
        responseCount: count,
        source,
      }, {
        correlationId,
        component: 'useResponses',
        op: 'useResponses_render',
        success: true,
      });
    };

    let usedCache = false;

    if (cacheEnabled) {
      const memoryPayload = getComparisonResponsesFromMemory(comparisonId);
      if (memoryPayload && latestRequestRef.current === requestId) {
        usedCache = true;
        setResponses(memoryPayload.responses.map(entry => entry.response));
        setLastUpdatedAt(memoryPayload.lastUpdated);
        setLoading(false);
        setRefreshing(true);
        setError(null);
        logRendered('memory', memoryPayload.responses.length);
      }

      if (!memoryPayload) {
        setLoading(true);
        const persisted = await hydrateComparisonResponses(comparisonId);
        if (persisted && latestRequestRef.current === requestId) {
          usedCache = true;
          setResponses(persisted.responses.map(entry => entry.response));
          setLastUpdatedAt(persisted.lastUpdated);
          setLoading(false);
          setRefreshing(true);
          setError(null);
          logRendered('indexeddb', persisted.responses.length);
        }
      }
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    logger.info('useResponses_fetch_start', {
      comparisonId,
      cacheEnabled,
      cacheVisible: usedCache,
    }, {
      correlationId,
      component: 'useResponses',
      op: 'useResponses_fetch'
    });

    try {
      const data = await fetchResponses(comparisonId, {
        bypassCache: !cacheEnabled,
        correlationId
      });

      if (latestRequestRef.current !== requestId) {
        logger.debug('useResponses_fetch_stale_result', { comparisonId }, {
          correlationId,
          component: 'useResponses',
          op: 'useResponses_fetch'
        });
        return;
      }

      setResponses(data.responses);
      setLastUpdatedAt(data.meta.timestamp ?? null);
      setLoading(false);
      setRefreshing(false);

      const networkSource = data.meta.source === 'network'
        ? (usedCache ? 'network_refresh' : 'network')
        : 'not_modified';
      logRendered(networkSource, data.responses.length);

      logger.info('useResponses_fetch_success', {
        comparisonId,
        responseCount: data.responses.length,
        source: data.meta.source,
      }, {
        correlationId,
        component: 'useResponses',
        op: 'useResponses_fetch',
        success: true
      });
    } catch (err) {
      if (latestRequestRef.current !== requestId) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to fetch responses';
      setError(message);
      if (!usedCache) {
        setResponses([]);
        setLoading(false);
      }
      setRefreshing(false);

      logger.error('useResponses_fetch_error', { comparisonId }, {
        correlationId,
        component: 'useResponses',
        op: 'useResponses_fetch',
        success: false,
        err_message: message
      });
    }
  }, [comparisonId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    responses,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    refetch: fetchData,
  };
}
